/**
 * Parse a CSS color string into a 0..1 RGBA tuple.
 *
 * Handles: `#rgb`, `#rrggbb`, `#rrggbbaa`, `rgb(r,g,b)`, `rgba(r,g,b,a)`,
 * and a handful of common named colors. Unknown formats fall back to opaque
 * white so shaders don't divide by NaN.
 */
export function parseColor(input: string): [number, number, number, number] {
  const s = input.trim().toLowerCase();
  // hex
  if (s.startsWith('#')) {
    const hex = s.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16) / 255;
      const g = parseInt(hex[1] + hex[1], 16) / 255;
      const b = parseInt(hex[2] + hex[2], 16) / 255;
      return [r, g, b, 1];
    }
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16) / 255;
      const g = parseInt(hex.slice(2, 4), 16) / 255;
      const b = parseInt(hex.slice(4, 6), 16) / 255;
      return [r, g, b, 1];
    }
    if (hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16) / 255;
      const g = parseInt(hex.slice(2, 4), 16) / 255;
      const b = parseInt(hex.slice(4, 6), 16) / 255;
      const a = parseInt(hex.slice(6, 8), 16) / 255;
      return [r, g, b, a];
    }
  }
  // rgb / rgba — channels are 0..255 ints (clamped); alpha allows leading `-` so
  // out-of-range strings still parse and clamp instead of falling through to white.
  const rgbMatch = s.match(/^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)\s*(?:,\s*(-?[0-9.]+)\s*)?\)$/);
  if (rgbMatch) {
    const r = clamp01(parseFloat(rgbMatch[1]) / 255);
    const g = clamp01(parseFloat(rgbMatch[2]) / 255);
    const b = clamp01(parseFloat(rgbMatch[3]) / 255);
    const a = rgbMatch[4] != null ? clamp01(parseFloat(rgbMatch[4])) : 1;
    return [r, g, b, a];
  }
  // named (small set covering defaultEffect + common usages)
  const named: Record<string, [number, number, number, number]> = {
    white: [1, 1, 1, 1],
    black: [0, 0, 0, 1],
    transparent: [0, 0, 0, 0],
    red: [1, 0, 0, 1],
    green: [0, 0.5, 0, 1],
    blue: [0, 0, 1, 1],
  };
  if (named[s]) return named[s];
  return [1, 1, 1, 1];
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}
